import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useGender } from "@/contexts/GenderContext";
import { FounderBadge } from "@/components/FounderBadge";
import { OfficialBearInsiderBadge } from "@/components/OfficialBearInsiderBadge";
import { OfficialBearInsiderTooltip } from "@/components/OfficialBearInsiderTooltip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Book, User, Settings, LogOut, Menu, ShoppingBag, Info, Users, HelpCircle, Code, Sparkles, Palette, PersonStanding } from "lucide-react";
import { SkipNavigation } from "./SkipNavigation";

export default function Navigation() {
  const { user, logoutMutation, isAuthenticated, isLoading, isFounder, hasActiveSubscription, isOfficialBearInsider } = useAuth();
  const { selectedGender, getPersonalizedName } = useGender();
  const [location] = useLocation();

  const getUserInitials = () => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getLogoSrc = () => {
    switch (selectedGender) {
      case 'girl':
        return '/logo-pink.png';
      case 'boy':
        return '/logo-blue.png';
      case 'other':
        return '/logo-purple.png';
      default:
        return '/logo.png';
    }
  };

  const navItems = [
    { href: "/home", label: "Create Story", icon: Book },
    { href: "/stories", label: "My Stories", icon: Book },
    { href: "/character-studio", label: "Character Studio", icon: Palette },
    ...(hasActiveSubscription || isFounder ? [
      { href: "/characters", label: "Characters", icon: PersonStanding, premium: true }
    ] : []),
    { href: "/community", label: "Community", icon: Users },
    { href: "https://swag.SnuggleCrafters.com", label: "Swag Store", icon: ShoppingBag, external: true },
  ];

  return (
    <>
      <SkipNavigation />
      <nav className="bg-white/95 backdrop-blur-sm border-b border-primary/10 sticky top-0 z-50 shadow-sm" role="navigation" aria-label="Main navigation">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href={isAuthenticated ? "/home" : "/"} className="flex items-center space-x-2 group" aria-label={`${getPersonalizedName()}'s Stories - Home`}>
              <img 
                src={getLogoSrc()} 
                alt="SnuggleCrafters Logo" 
                className="w-12 h-12 object-contain group-hover:scale-105 transition-transform"
                data-testid="logo-image"
              />
              <span className="text-xl font-bold text-snuggle-gradient">
                {isAuthenticated ? `${getPersonalizedName()}'s Stories` : "SnuggleCrafters"}
              </span>
              {isFounder && (
                <div className="ml-2 hidden sm:block">
                  <FounderBadge size="small" animated={true} showLabel={false} />
                </div>
              )}
              {isOfficialBearInsider && (
                <div className="ml-2 hidden sm:block">
                  <OfficialBearInsiderTooltip showShield={true} size="small">
                    <span className="text-sm font-medium text-amber-700">Official Bear Insider</span>
                  </OfficialBearInsiderTooltip>
                </div>
              )}
              {/* Mobile Official Bear Insider Badge */}
              {isOfficialBearInsider && (
                <div className="ml-2 sm:hidden">
                  <OfficialBearInsiderTooltip showShield={true} size="small">
                    <OfficialBearInsiderBadge size="small" animated={true} showLabel={false} />
                  </OfficialBearInsiderTooltip>
                </div>
              )}
            </Link>

            {isAuthenticated ? (
              <>
                {/* Desktop Navigation - Text + Icons on lg+, Icons only on md */}
                <div className="hidden lg:flex items-center space-x-4">
                  {navItems.map((item) => {
                    if (item.external) {
                      return (
                        <a
                          key={item.href}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-primary/10"
                          style={{ 
                            color: "#000000",
                            transition: "color 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#7c3aed";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#000000";
                          }}
                        >
                          {item.icon && <item.icon className="h-4 w-4" />}
                          <span>{item.label}</span>
                          {(item as any).premium && (
                            <Sparkles className="h-3 w-3 ml-1 text-amber-500" />
                          )}
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          location === item.href
                            ? "bg-primary shadow-md"
                            : "hover:bg-primary/10"
                        }`}
                        style={{ 
                          color: location === item.href ? "#000000" : "#000000",
                          transition: "color 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          if (location !== item.href) {
                            e.currentTarget.style.color = "#7c3aed";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (location !== item.href) {
                            e.currentTarget.style.color = "#000000";
                          }
                        }}
                        data-testid={`nav-${item.href.replace('/', '')}`}
                      >
                        {item.icon && <item.icon className="h-4 w-4" />}
                        <span>{item.label}</span>
                        {(item as any).premium && (
                          <Sparkles className="h-3 w-3 ml-1 text-amber-500" />
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* Medium Screen Navigation - Icons only */}
                <div className="hidden md:flex lg:hidden items-center space-x-2">
                  {navItems.map((item) => {
                    if (item.external) {
                      return (
                        <a
                          key={item.href}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-2 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-primary/10"
                          style={{ 
                            color: "#000000",
                            transition: "color 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#7c3aed";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#000000";
                          }}
                          title={item.label}
                          aria-label={item.label}
                        >
                          {item.icon && <item.icon className="h-5 w-5" />}
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`flex items-center justify-center p-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          location === item.href
                            ? "bg-primary shadow-md"
                            : "hover:bg-primary/10"
                        }`}
                        style={{ 
                          color: location === item.href ? "#9333EA" : "#000000",
                          transition: "color 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          if (location !== item.href) {
                            e.currentTarget.style.color = "#7c3aed";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (location !== item.href) {
                            e.currentTarget.style.color = "#000000";
                          }
                        }}
                        title={item.label}
                        aria-label={item.label}
                        data-testid={`nav-${item.href.replace('/', '')}`}
                      >
                        {item.icon && <item.icon className="h-5 w-5" />}
                      </Link>
                    );
                  })}
                </div>

                {/* User Menu */}
                <div className="flex items-center space-x-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-8 w-8 rounded-full" aria-label="User menu" aria-expanded="false" aria-haspopup="menu">
                        <Avatar className="h-8 w-8">
                          {user?.profilePictureUrl && (
                            <AvatarImage 
                              src={user.profilePictureUrl} 
                              alt={`${user.firstName || 'User'}'s profile picture`}
                            />
                          )}
                          <AvatarFallback className="bg-gray-200 text-black">
                            {getUserInitials() || "U"}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-white border border-gray-200 shadow-lg" align="end" forceMount>
                      <div className="flex flex-col space-y-1 p-2 border-b border-gray-100">
                        <p className="text-sm font-medium leading-none text-gray-900">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs leading-none text-gray-500">
                          {user?.email}
                        </p>
                      </div>
                      <div className="p-1">
                        <DropdownMenuItem asChild>
                          <Link href="/profile" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                            <User className="mr-2 h-4 w-4" />
                            <span>Profile & Settings</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/child-profiles" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }} data-testid="nav-child-profiles">
                            <Users className="mr-2 h-4 w-4" />
                            <span>Child Profiles</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/subscribe" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Subscription</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/help" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                            <HelpCircle className="mr-2 h-4 w-4" />
                            <span>Help Center</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/developer-portal" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                            <Code className="mr-2 h-4 w-4" />
                            <span>Developer API</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link href="/about" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                            <Info className="mr-2 h-4 w-4" />
                            <span>About</span>
                          </Link>
                        </DropdownMenuItem>
                      </div>
                      <div className="border-t border-gray-100 p-1">
                        <DropdownMenuItem
                          className="flex items-center cursor-pointer px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                          onClick={() => logoutMutation.mutate()}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>Sign out</span>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Mobile menu button */}
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label="Mobile navigation menu">
                          <Menu className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-64 bg-white border border-gray-200 shadow-lg" align="end">
                        {/* Official Bear Insider Badge at the top of mobile menu */}
                        {isOfficialBearInsider && (
                          <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-yellow-50">
                            <OfficialBearInsiderTooltip showShield={true} size="medium">
                              <OfficialBearInsiderBadge 
                                size="small" 
                                animated={true} 
                                showLabel={true}
                                className="w-full justify-center"
                              />
                            </OfficialBearInsiderTooltip>
                          </div>
                        )}
                        {navItems.map((item) => (
                          <DropdownMenuItem key={item.href} asChild>
                            {item.external ? (
                              <a href={item.href} target="_blank" rel="noopener noreferrer" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                                <span>{item.label}</span>
                              </a>
                            ) : (
                              <Link href={item.href} className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }} data-testid={`nav-mobile-${item.href.replace('/', '')}`}>
                                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                                <span>{item.label}</span>
                              </Link>
                            )}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="my-1" />
                        <DropdownMenuItem
                          className="flex items-center cursor-pointer px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                          onClick={() => logoutMutation.mutate()}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>Logout</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </>
            ) : (
              // Unauthenticated navigation
              <div className="flex items-center space-x-4">
                <Link href="/auth">
                  <Button variant="outline" size="sm">Sign In</Button>
                </Link>
                <Link href="/auth">
                  <Button size="sm" className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}